import React, { useMemo } from 'react';
import { themes } from '../config/themes';
import { getLocalDateKey } from '../utils/eventUtils';
import SkylineWatermark from './calendar/SkylineWatermark';
import CalendarLegend from './calendar/CalendarLegend';
import EventChip from './calendar/EventChip';
import { fs, sz } from '../utils/typography';

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MAX_CHIPS = 3;

// Month view — "Atlanta Editorial" design refresh: charcoal weekday band,
// hairline grid of translucent day cells over the bottom-anchored skyline
// watermark, bar-treatment event chips, accent today pill, legend footer.
const MonthView = ({ curMonth, curYear, events, theme, onDaySelect, onEventSelect, selectedDay }) => {
    const tokens = theme?.tokens || themes.light.tokens;
    const c = tokens.colors;

    const firstDay = new Date(curYear, curMonth - 1, 1).getDay();
    const lastDay = new Date(curYear, curMonth, 0).getDate();
    const curMonthName = new Date(curYear, curMonth - 1, 1).toLocaleString('default', { month: 'long' });
    const totalCells = lastDay + firstDay > 35 ? 42 : 35;
    const rows = totalCells / 7;

    const eventsByDay = useMemo(() => {
        const map = {};
        (events || []).forEach((event) => {
            const key = getLocalDateKey(event.startDate);
            if (key) (map[key] = map[key] || []).push(event);
        });
        return map;
    }, [events]);

    const todayKey = getLocalDateKey(new Date());
    const selectedKey = selectedDay ? getLocalDateKey(selectedDay) : null;

    return (
        <div
            className="w-full h-full flex flex-col"
            style={{ background: c.surface, color: c.ink, fontFamily: tokens.fonts.ui, textAlign: 'left' }}
            role="region"
            aria-label={`Calendar for ${curMonthName} ${curYear}`}
        >
            {/* charcoal weekday header band */}
            <div
                className="grid grid-cols-7 flex-none"
                style={{ background: c.headBg, borderBottom: `1px solid ${c.gridLine}`, position: 'relative', zIndex: 1 }}
                role="row"
            >
                {DOW.map((day) => (
                    <div
                        key={day}
                        className="text-center py-2 px-1"
                        style={{
                            fontFamily: tokens.fonts.ui, fontSize: fs(11), fontWeight: 600,
                            letterSpacing: 0.8, textTransform: 'uppercase', color: c.headInk,
                        }}
                        role="columnheader"
                    >
                        <span className="hidden lg:inline">{day}</span>
                        <span className="inline lg:hidden">{day[0]}</span>
                    </div>
                ))}
            </div>

            {/* day grid over the skyline watermark */}
            <div
                className="flex-1 min-h-0 grid grid-cols-7"
                style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, position: 'relative' }}
                role="grid"
                aria-label={`Days in ${curMonthName} ${curYear}`}
            >
                <SkylineWatermark tokens={tokens} />
                {Array.from({ length: totalCells }, (_, i) => {
                    const dayNum = i - firstDay + 1;
                    const inMonth = dayNum >= 1 && dayNum <= lastDay;
                    const col = i % 7;
                    const row = Math.floor(i / 7);

                    if (!inMonth) {
                        return <div key={`blank-${i}`} role="gridcell" aria-hidden="true" style={{ background: 'transparent' }} />;
                    }

                    const thisDate = new Date(curYear, curMonth - 1, dayNum);
                    const dateKey = getLocalDateKey(thisDate);
                    const dayEvents = eventsByDay[dateKey] || [];
                    const isToday = dateKey === todayKey;
                    const isSelected = dateKey === selectedKey;
                    const shown = dayEvents.slice(0, MAX_CHIPS);
                    const extra = dayEvents.length - shown.length;

                    return (
                        <div
                            key={dateKey}
                            className="flex flex-col min-w-0 overflow-hidden cursor-pointer"
                            style={{
                                borderLeft: col === 0 ? 'none' : `1px solid ${c.gridLine}`,
                                borderTop: row === 0 ? 'none' : `1px solid ${c.gridLine}`,
                                background: isSelected
                                    ? `color-mix(in srgb, ${c.selBg} 84%, transparent)`
                                    : `color-mix(in srgb, ${c.surface} 60%, transparent)`,
                                boxShadow: isSelected ? `inset 0 0 0 1.5px ${c.selRing}` : 'none',
                                padding: '6px 7px 5px', position: 'relative', zIndex: 1,
                            }}
                            role="gridcell"
                            tabIndex={0}
                            onClick={() => onDaySelect(thisDate)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onDaySelect(thisDate);
                                }
                            }}
                            aria-label={`${thisDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}`}
                        >
                            <div className="flex items-center justify-between flex-none" style={{ marginBottom: 3 }}>
                                <span style={{
                                    fontFamily: tokens.fonts.ui, fontSize: fs(12.5), fontWeight: 600, lineHeight: 1,
                                    color: isToday ? c.todayInk : c.ink,
                                    ...(isToday ? {
                                        background: c.today, borderRadius: sz(13), minWidth: sz(21), height: sz(21),
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                                    } : { padding: '4px 2px' }),
                                }}>{dayNum}</span>
                            </div>

                            {/* event chips — desktop */}
                            <div
                                className="hidden lg:flex flex-col flex-1 min-h-0"
                                style={{ gap: 2, paddingBottom: extra > 0 ? sz(22) : 0 }}
                            >
                                {shown.map((event) => (
                                    <EventChip
                                        key={event.id}
                                        event={event}
                                        tokens={tokens}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDaySelect(thisDate);
                                            onEventSelect(event, e);
                                        }}
                                    />
                                ))}
                            </div>

                            {extra > 0 && (
                                <button
                                    type="button"
                                    className="hidden lg:flex items-center justify-center"
                                    style={{
                                        position: 'absolute', left: 0, right: 0, bottom: 0, height: sz(22),
                                        border: 'none', borderTop: `1px solid ${c.gridLine}`,
                                        background: `color-mix(in srgb, ${c.accent} ${tokens.mode === 'dark' ? '22%' : '12%'}, ${c.surfaceAlt})`,
                                        color: c.accent, cursor: 'pointer',
                                        fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 700,
                                        letterSpacing: 0.4, textTransform: 'uppercase',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDaySelect(thisDate);
                                    }}
                                    aria-label={`Show ${extra} more event${extra > 1 ? 's' : ''} for ${thisDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
                                >
                                    +{extra} more
                                </button>
                            )}

                            {/* event count pill — small screens */}
                            {dayEvents.length > 0 && (
                                <div className="flex lg:hidden items-center justify-center flex-1">
                                    <span style={{
                                        fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 600,
                                        color: c.accent, border: `1px solid ${c.accent}`, borderRadius: 11,
                                        minWidth: 22, padding: '2px 6px', textAlign: 'center',
                                    }}>{dayEvents.length}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <CalendarLegend tokens={tokens} />
        </div>
    );
};

export default MonthView;
